<template>
  <div id="paginacao" v-show="totalPaginas > 1">
    <button @click="mudarPagina(paginaAtualEfetiva - 1)" :disabled="paginaAtualEfetiva === 0">
      <span class="texto-botao">Voltar</span>
      <span class="icone-botao">&lt;</span>
    </button>

    <div class="paginas">
      <div class="paginas-disponiveis" :class="{ 'pagina-selecionada': paginaAtualEfetiva === 0 }"
        @click="mudarPagina(0)">
        1
      </div>

      <div class="sinal-pagina" v-if="mostrarReticenciasEsquerda">
        ...
      </div>

      <div v-for="pagina in paginasVisiveis" :key="pagina" class="paginas-disponiveis"
        :class="{ 'pagina-selecionada': paginaAtualEfetiva === pagina - 1 }" @click="mudarPagina(pagina - 1)">
        {{ pagina }}
      </div>

      <div class="sinal-pagina" v-if="mostrarReticenciasDireita">
        ...
      </div>

      <div v-if="totalPaginas > 1" class="paginas-disponiveis" :class="{
        'pagina-selecionada': paginaAtualEfetiva === totalPaginas - 1,
      }" @click="mudarPagina(totalPaginas - 1)">
        {{ totalPaginas }}
      </div>
    </div>

    <button @click="mudarPagina(paginaAtualEfetiva + 1)" :disabled="paginaAtualEfetiva >= totalPaginas - 1">
      <span class="texto-botao">Avançar</span>
      <span class="icone-botao">&gt;</span>
    </button>
  </div>
</template>

<script setup>
import { computed } from "vue";
import { useRoute } from "vue-router";

const props = defineProps({
  totalPaginas: {
    type: Number,
    required: true,
  },
  intervalo: {
    type: Number,
    default: 3,
  },
  paginaAtual: {
    type: Number,
    default: undefined,
  },
  rolarParaTopo: {
    type: Boolean,
    default: true,
  },
});

const route = useRoute();

const paginaAtualEfetiva = computed(() => {
  if (props.paginaAtual !== undefined && props.paginaAtual !== null) {
    return props.paginaAtual;
  }
  return Number(route.query.page ?? 0);
});

const emit = defineEmits(["mudar-pagina"]);

function mudarPagina(pagina) {
  if (pagina < 0 || pagina >= props.totalPaginas) return;

  emit("mudar-pagina", pagina);

  if (props.rolarParaTopo) {
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  }
}

const intervaloResponsivo = computed(() => {
  if (typeof window !== "undefined" && window.innerWidth <= 600) {
    return 1;
  }
  return props.intervalo;
});

const paginasVisiveis = computed(() => {
  const inicio = Math.max(2, paginaAtualEfetiva.value - intervaloResponsivo.value + 1);
  const fim = Math.min(
    props.totalPaginas - 1,
    paginaAtualEfetiva.value + intervaloResponsivo.value + 1
  );
  const paginas = [];
  for (let i = inicio; i <= fim; i++) {
    paginas.push(i);
  }
  return paginas;
});

const mostrarReticenciasEsquerda = computed(() => {
  return paginasVisiveis.value.length > 0 && paginasVisiveis.value[0] > 2;
});

const mostrarReticenciasDireita = computed(() => {
  const ultima = paginasVisiveis.value[paginasVisiveis.value.length - 1];
  return paginasVisiveis.value.length > 0 && ultima < props.totalPaginas - 1;
});
</script>

<style scoped>
#paginacao {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 16px;
  margin: 20px 0;
  flex-wrap: wrap;
}

#paginacao button {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 6px 18px;
  border-radius: 8px;
  background-color: var(--roxo-primario);
  color: var(--branco);
  border: none;
  cursor: pointer;
  font-weight: 500;
  transition: background-color 0.2s;
  height: 40px;
}

#paginacao button:hover:not(:disabled) {
  background-color: var(--roxo-claro);
}

#paginacao button:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

.icone-botao {
  display: none;
}

.paginas {
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  justify-content: center;
}

.paginas-disponiveis {
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  background-color: var(--roxo-primario);
  color: var(--branco);
  height: 40px;
  width: 40px;
  font-size: 15px;
  cursor: pointer;
  transition: background-color 0.2s;
  user-select: none;
}

.paginas-disponiveis:hover {
  background-color: var(--roxo-claro);
}

.pagina-selecionada {
  background-color: var(--roxo-claro);
}

.sinal-pagina {
  color: var(--roxo-primario);
  font-weight: bold;
  padding: 0 4px;
  user-select: none;
}

@media (max-width: 600px) {
  #paginacao {
    gap: 8px;
    margin: 16px 0;
  }

  #paginacao button {
    padding: 6px 12px;
    height: 34px;
    font-size: 14px;
  }

  .paginas {
    gap: 4px;
  }

  .paginas-disponiveis {
    height: 34px;
    width: 34px;
    font-size: 13px;
  }
}

@media (max-width: 400px) {
  #paginacao {
    gap: 6px;
  }

  #paginacao button {
    padding: 0;
    width: 34px;
    border-radius: 50%;
  }

  .texto-botao {
    display: none;
  }

  .icone-botao {
    display: inline;
    font-size: 16px;
    font-weight: bold;
  }

  .paginas-disponiveis {
    height: 30px;
    width: 30px;
    font-size: 12px;
  }

  .sinal-pagina {
    font-size: 12px;
    padding: 0 2px;
  }
}
</style>